import React, { useEffect, useState } from "react";
import withLayout from "../../../hocs/Layout";
import { apiConstants, getDownload, postMultipart } from "../../../common/constants";
import { FormProvider, RHFFileField, RHFSelectField } from "../../../common/hook-form";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { Box, Button, Grid, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FileDownloadOutlined } from "@mui/icons-material";
import PageTitle from "../../../common/PageTitle";
import BulkOrderResults from "./BulkOrderResult";
import { useSnackbar } from "notistack";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, PRIMARY_BLUE, SUBTLE } from '../../../styles/foundation/colors';
import {
  pageContainerSx,
  uploadSectionSx, primaryBtnSx, secondaryBtnSx, helpTextSx, helpTextTypeSx,
} from '../../../styles/patterns';

// Uploads a CSV of D2C orders and stages them for review (no courier allocated yet) —
// distinct from "Create Bulk Shipments", which allocates a courier immediately per row.
// Staged orders show up on the Channel Orders page (same as Shopify/WooCommerce/PrestaShop
// orders), where they're selected and turned into shipments (courier chosen at that step,
// not here).
//
// Order type here is sample-selection only, not sent to the backend: this CSV format already
// carries order_type/is_reverse per row (unlike the older Bulk Shipments page's two separate,
// single-type-per-file templates), so one upload can already mix Forward and Reverse orders.
const ORDER_TYPE_OPTIONS = ["Forward", "Reverse"];

const BulkOrderUpload = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [bulkOrderIdentifier, setBulkOrderIdentifier] = useState('');
  const [sampleName, setSampleName] = useState();
  const merchant = getMerchant();
  const merchantId = merchant.id ? merchant.id : ''
  const [reload, setReload] = useState(0)

  const methodsBulkUpload = useForm({
    resolver: yupResolver(Yup.object().shape({ orderFile: Yup.mixed().required("Please Select a File") })),
    defaultValues: { orderFile: '' }
  });
  const { handleSubmit: handleSubmitBulkUpload, reset: resetForm } = methodsBulkUpload;

  useEffect(() => {
    if (bulkOrderIdentifier === "Forward") setSampleName("FORWARD_BULK_ORDER_UPLOAD_STAGED");
    if (bulkOrderIdentifier === "Reverse") setSampleName("REVERSE_BULK_ORDER_UPLOAD_STAGED");
  }, [bulkOrderIdentifier]);

  const handleBulkSample = () => {
    getDownload(`${apiConstants.BASE_LOCAL}${apiConstants.DOWNLOAD_SAMPLE_LOCATION}?fileName=${sampleName}`)
      .then((response) => {
        const binaryData = [response.data];
        const href = URL.createObjectURL(new Blob(binaryData, { type: "application/octet-stream" }));
        const link = document.createElement("a");
        link.href = href; link.setAttribute("download", "bulk-order-upload-sample.csv");
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        URL.revokeObjectURL(href);
      }).catch((err) => console.error(err));
  };

  const onSubmitBulkUpload = async (data) => {
    setIsUploadingFile(true)
    let form = new FormData();
    form.append("file", data.orderFile[0])
    await postMultipart(`${apiConstants.BULK_ORDER_UPLOAD}?merchantOid=${merchantId}`, form).then((res) => {
      setReload(reload + 1);
      if (res.status === 200) enqueueSnackbar("Successfully Uploaded!", { variant: "success" });
      else enqueueSnackbar(`${res.data}`, { variant: 'error' })
    }).catch((err) => {
      enqueueSnackbar(err?.response?.data?.message || "Error uploading orders", { variant: 'error' })
    }).finally(() => {
      setIsUploadingFile(false)
      resetForm({ orderFile: '' })
    })
  };

  return (
    <Box sx={props.embedded ? undefined : pageContainerSx}>
      <PageTitle subTitle="Upload a CSV file to stage multiple orders for review — no shipment is created until you select and confirm them on the Channel Orders page">Create Bulk Orders</PageTitle>
      <Box sx={uploadSectionSx}>
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY, mb: 1.5 }}>
          Upload Order File
        </Typography>

        <Box sx={helpTextSx}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
          <Typography sx={helpTextTypeSx}>
            <strong>Instructions:</strong> Select an order type to download the matching sample CSV, fill in the
            required columns, and upload. Orders are staged for review, not shipped yet — go to the
            <strong> Channel Orders</strong> page to select courier and create shipments.
          </Typography>
        </Box>

        <FormProvider methods={methodsBulkUpload} onSubmit={handleSubmitBulkUpload(onSubmitBulkUpload)}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <RHFSelectField name="bulkOrderIdentifier" label="Select Order Type"
                options={ORDER_TYPE_OPTIONS.map((val) => ({ value: val, label: val }))}
                value={bulkOrderIdentifier} width="100%" onChange={(e) => setBulkOrderIdentifier(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <RHFFileField name="orderFile" accept={["csv"]} required={true} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <LoadingButton startIcon={<FileUploadIcon />} type="submit" loading={isUploadingFile}
                  variant="contained" size="small" sx={primaryBtnSx}>
                  Upload
                </LoadingButton>
                {sampleName ? (
                  <Button variant="outlined" size="small" sx={secondaryBtnSx}
                    startIcon={<FileDownloadOutlined />} onClick={handleBulkSample}>
                    Sample Download
                  </Button>
                ) : (
                  <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                    Select order type to download sample
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </FormProvider>
      </Box>

      <BulkOrderResults resultType="BULK_ORDER_UPLOAD" reload={reload} />
    </Box>
  );
};
export default withLayout(BulkOrderUpload);
